import { FC, PropsWithChildren, useEffect, useState } from 'react'
import styled from 'styled-components'
import { TextEllipsis } from '@/components/common'
import { ModalBaseProps } from '@/components/common/modals/ModalBase'
import ModalSliderPortal from '@/components/common/modals/ModalSliderPortal'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { LAYOUT_UNITS, Z_INDEXES } from '@/constants/legacy/constLayout'
import { enableScroll } from '@/utils/utilCommon'

const MODAL_ANIMATION_DURATION = 100

type ModalSliderProps<T> = {
  title?: string
  centerTitle?: boolean
  titleNoBorder?: boolean
  noHeader?: boolean
} & ModalBaseProps<T>

const ModalSliderDefault: FC<PropsWithChildren<ModalSliderProps<unknown>>> = ({
  title,
  centerTitle = false,
  titleNoBorder = false,
  children,
  onCancel,
  useModalProps,
  noHeader = false,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const { visible: isAnimation, hideModal: fadeOutAnimation } = useModalProps

  useEffect(() => {
    const openModal = () => isAnimation && setIsOpen(true)
    const disableScroll = () => enableScroll(!isOpen)
    openModal()
    disableScroll()

    return () => {
      if (!isOpen) {
        return
      }
      setTimeout(() => setIsOpen(false), MODAL_ANIMATION_DURATION)
    }
  }, [isAnimation, isOpen])

  useEffect(() => {
    return () => {
      enableScroll(true)
    }
  }, [])

  const handleClose = () => {
    onCancel?.()
    fadeOutAnimation()
  }

  if (!isOpen) {
    return null
  }

  return (
    <ModalSliderPortal>
      <StyledContainer data-cy="modal-slider">
        <Popup isAnimation={isAnimation}>
          {!noHeader && (
            <TitleRow border={titleNoBorder ? 0 : 1}>
              <TextEllipsis size={18} weight="bold" lineNumber={1} align={centerTitle ? 'center' : 'left'}>
                {title}
              </TextEllipsis>
            </TitleRow>
          )}
          {children}
        </Popup>
        <Background isAnimation={isAnimation} onClick={handleClose} />
      </StyledContainer>
    </ModalSliderPortal>
  )
}

const StyledContainer = styled.div`
  position: fixed;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  z-index: ${Z_INDEXES.BOTTOM_SLIDE_POPUP};
`

const Background = styled.div<{ isAnimation: boolean }>`
  width: 100%;
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  height: 100vh;
  position: fixed;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: ${({ isAnimation }) => (isAnimation ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0)')};
  transition: background-color 0.3s ease;
`

const Popup = styled.div<{ isAnimation: boolean }>`
  z-index: ${Z_INDEXES.BOTTOM_SLIDE_POPUP};
  position: absolute;
  bottom: 0;
  display: block;
  width: 100%;
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  max-height: calc(100% - 50px);
  overflow-y: scroll;
  border-radius: 20px 20px 0 0;
  background-color: ${(props) => props.theme.color.grayWhite};
  animation: ${({ isAnimation }) => (isAnimation ? `0.3s forwards slideIn` : `0.2s ease forwards slideOut`)};

  @keyframes slideIn {
    0% {
      transform: translateY(100%);
    }
    100% {
      transform: translateY(0%);
    }
  }

  @keyframes slideOut {
    0% {
      transform: translateY(0%);
    }
    100% {
      transform: translateY(100%);
    }
  }
`

const TitleRow = styled.div<{ border: number }>`
  position: relative;
  height: 54px;
  border-bottom: ${({ border }) => (border ? `1px solid ${GlobalColorEnum.Gray200}` : 'none')};
  background-color: ${(props) => props.theme.color.grayWhite};
  width: 100%;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  z-index: ${Z_INDEXES.BOTTOM_SLIDE_POPUP + 1};
  max-width: ${LAYOUT_UNITS.BODY_MAX_WIDTH};
  margin: auto 0;
  padding: 16px 20px;
`

export default ModalSliderDefault
